import { AddMember } from '@icons';

import { ActionButton, Button, InfiniteScroll } from '@components';
import CustomTable from '@components/custom-table/CustomTable';
import { DEFAULT_PLACEHOLDER } from '@constants/generic';
import {
  attendanceBasedAddMemberTableColumn,
  PaymentCollectionType
} from '@features/group-management/constants';
import {
  formatDueDatePlaceholder,
  getAdvancedByTermFrequencyPlaceholder
} from '@features/group-management/helper';
import useCurrency from '@hooks/useCurrency';
import { AddManuallyTabProps, MemberListType } from '@types';
import { formatISODate } from '@utils/date';

import theme from '../../../../../../../themes/colors';

const getMonthDayLabel = (activationDate: string): string => {
  if (activationDate === 'first_day') return '1st day of the month';
  if (activationDate === 'last_day') return 'Last day of the month';
  if (activationDate && !Number.isNaN(Number(activationDate))) {
    const n = Number(activationDate);
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    const suffix = s[(v - 20) % 10] || s[v] || s[0];
    return `${n}${suffix} day of the month`;
  }
  return '1st day of the month';
};

const AddManuallyTab = ({
  memberList,
  handleAdd,
  handleEdit,
  handleDelete,
  maxTableHeight,
  columnHeaderList,
  infiniteScrollRequired = false,
  setPage = () => {},
  isLoading = false,
  disablePagination = false,
  groupAmount,
  isImmediatePayment = false,
  isCustomTerm = false,
  isAttendanceBasedFee = false,
  isSessionBased = false,
  groupSessionsPerCycle,
  defaultTermLabel
}: AddManuallyTabProps) => {
  const { formatCurrencyValue } = useCurrency();

  const handleMemberEdit = (event: React.MouseEvent, memberId: string) => {
    event.stopPropagation();
    handleEdit(memberId);
  };
  const handleMemberDelete = (event: React.MouseEvent, memberId: string) => {
    event.stopPropagation();
    handleDelete(memberId);
  };
  const displayAdvancedFrequency = () => {
    if (isCustomTerm)
      return getAdvancedByTermFrequencyPlaceholder(defaultTermLabel);
    return DEFAULT_PLACEHOLDER;
  };

  const modifiedTableData = memberList?.map((row: MemberListType) => {
    const baseData = {
      id: row.name + row.primaryNumber,
      name: <p className="table w-full table-fixed break-words">{row.name}</p>,
      primaryNumber: `+${row.primaryNumberCountryCode} ${row?.primaryNumber}`,
      altNumber: row?.altNumber
        ? `+${row.altNumberCountryCode} ${row?.altNumber}`
        : DEFAULT_PLACEHOLDER,
      feeAmount: (
        <div className="flex items-center">
          {row.amount || groupAmount ? (
            <>
              <span className="pr-0.5 font-inter font-normal">
                {formatCurrencyValue(Number(row.amount || groupAmount)).symbol}
              </span>
              <span>{row.amount || groupAmount}</span>
            </>
          ) : (
            DEFAULT_PLACEHOLDER
          )}
        </div>
      ),
      actions: (
        <div className="flex gap-4">
          <ActionButton
            id={row.id}
            handleDelete={handleMemberDelete}
            handleEdit={handleMemberEdit}
          />
        </div>
      )
    };

    if (isAttendanceBasedFee) {
      let billingValue: string | number = DEFAULT_PLACEHOLDER;
      if (isSessionBased) {
        billingValue =
          row?.sessionsPerCycle || groupSessionsPerCycle || DEFAULT_PLACEHOLDER;
      } else if (row?.dueDate) {
        billingValue = getMonthDayLabel(row.dueDate);
      }

      return {
        ...baseData,
        billingPeriod: <div className="flex items-center">{billingValue}</div>
      };
    }

    return {
      ...baseData,
      dueDate: (
        <div className="flex items-center">
          {row?.dueDate && !isImmediatePayment && !isCustomTerm ? (
            <span>
              {
                formatDueDatePlaceholder(
                  row.paymentCollectionType as PaymentCollectionType,
                  Number(row.interval),
                  row.dueDate
                ) as string
              }
            </span>
          ) : (
            displayAdvancedFrequency()
          )}
        </div>
      ),
      startDate: row.startDate
        ? formatISODate({ date: row.startDate })
        : DEFAULT_PLACEHOLDER
    };
  });

  const tableColumns = isAttendanceBasedFee
    ? attendanceBasedAddMemberTableColumn
    : columnHeaderList;

  const renderEmptyList = () => (
    <div className="flex size-full flex-col items-center justify-center rounded-lg border border-dashed border-cofeeGreyBlue4 bg-cofeeNeutral1 p-6">
      <AddMember className="size-12" />
      <div className="my-4 text-sm font-normal text-cofeeNeutral10">
        Click to add members to the group
      </div>
      <Button
        variant="secondary"
        label="Add member"
        handleClick={handleAdd}
        leftIconName="plus"
        leftIconStroke={theme.primary}
      />
    </div>
  );

  return (
    <div>
      {memberList && memberList.length <= 0 ? (
        <div className="flex size-full items-center justify-center p-6">
          {renderEmptyList()}
        </div>
      ) : (
        <div className={`${maxTableHeight || 'h-150'} overflow-y-hidden px-6`}>
          <div className="mt-4">
            <CustomTable
              headers={tableColumns}
              data={modifiedTableData || []}
              className="!pb-0"
            />
          </div>
          {infiniteScrollRequired && (
            <InfiniteScroll
              handleScroll={() => {
                setPage((prevPage: number) => prevPage + 1);
              }}
              isDisable={disablePagination}
              isLoading={!!isLoading}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default AddManuallyTab;
